import { useNavigate, useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  postListProfiles,
  useGetSchedules,
  useGetAllRuns,
  useGetAllSourceTables,
} from '@/api/control/control-plane/control-plane';
import { useGetOrganizationProductEntitlements } from '@/api/auth/product-entitlements/product-entitlements';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { QueryErrorState } from '@/components/common/QueryErrorState';

import { Plus, ChevronRight, Zap } from 'lucide-react';
import type {
  ProfileDto,
  RunMetadataDto,
  AllSourceTableDto,
  ScheduleDto,
} from '@/api/control/missionImpactAPI.schemas';
import { useRoles } from '@/contexts/RolesContext';
import { useLanguage } from '@/contexts/LanguageContext';
import type { Translations } from '@/i18n/types';
import { logger } from '@/services/logger';
import { calculateNextSyncTime, formatNextSyncTime } from '@/utils/nextSyncTime';
import { readBurstExpiresUtc } from '@/api/burst-control';
import { useEffect, useState, useMemo } from 'react';
import { getRegionLabel } from '@/constants/regions';
import { PageHeader } from '@/components/common/PageHeader';

/**
 * Format last sync time relative to now
 */
function formatLastSyncTime(run: RunMetadataDto | null, t: Translations): string {
  if (!run) return t.profiles.sync.noRecentSyncs;

  const timestamp = run.startedUtc ?? run.scheduledUtc;
  if (!timestamp) return t.profiles.sync.noRecentSyncs;

  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return t.profiles.sync.justNow;
  if (diffMins < 60) return `${String(diffMins)}${t.profiles.sync.minutesAgo}`;
  if (diffHours < 24) return `${String(diffHours)}${t.profiles.sync.hoursAgo}`;
  if (diffDays < 7) return `${String(diffDays)}${t.profiles.sync.daysAgo}`;

  return date.toLocaleDateString();
}

/**
 * Individual profile row component that fetches its own data
 */
interface ProfileRowProps {
  profile: ProfileDto;
  orgId: string;
  onView: () => void;
  t: Translations;
}

function ProfileRow({ profile, orgId, onView, t }: ProfileRowProps) {
  const [sourceTables, setSourceTables] = useState<AllSourceTableDto[]>([]);
  const [lastRun, setLastRun] = useState<RunMetadataDto | null>(null);
  const [schedules, setSchedules] = useState<ScheduleDto[]>([]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  // Fetch schedules
  const { data: schedulesData } = useGetSchedules(orgId, profile.id, {
    query: {
      enabled: !!orgId && !!profile.id,
      staleTime: 1000 * 60 * 5,
    },
  });

  // Fetch source tables
  const { mutate: fetchSourceTables } = useGetAllSourceTables({
    mutation: {
      onSuccess: (response) => {
        setSourceTables(response.data.items ?? []);
      },
    },
  });

  // Fetch last 24 hours of runs, just need the most recent one
  // We memoize the date to avoid refetching on every render
  const twentyFourHoursAgo = useMemo(() => {
    const date = new Date();
    date.setHours(date.getHours() - 24);
    return date;
  }, []);

  // Fetch last run using query hook
  const { data: runsData } = useGetAllRuns(
    orgId,
    {
      pageSize: 1,
      profileIds: [profile.id],
      startDate: twentyFourHoursAgo.toISOString(),
    },
    {
      query: {
        enabled: !!orgId && !!profile.id,
        staleTime: 1000 * 60 * 5,
      },
    }
  );

  // Fetch source tables on mount
  useEffect(() => {
    if (profile.id) {
      fetchSourceTables({
        orgId,
        data: {
          profileIds: [profile.id],
          onlyEnabled: true,
        },
      });
    }
  }, [profile.id, orgId]);

  // Update last run when data changes
  useEffect(() => {
    if (runsData?.data) {
      const runs = runsData.data.items ?? [];
      if (runs.length > 0) {
        setLastRun(runs[0]);
      }
      setIsDataLoaded(true);
    }
  }, [runsData]);

  useEffect(() => {
    if (schedulesData?.data) {
      setSchedules(schedulesData.data);
    }
  }, [schedulesData]);

  const burstExpiresUtc = readBurstExpiresUtc(profile);
  const isBurstActive = burstExpiresUtc != null && new Date(burstExpiresUtc).getTime() > Date.now();
  const nextSyncTime = calculateNextSyncTime(schedules, new Date(), burstExpiresUtc);
  const quietTimeCount = schedules.length;

  return (
    <TableRow className="cursor-pointer group" onClick={onView}>
      <TableCell className="font-medium">
        <span className="flex items-center gap-1.5">
          {profile.name}
          {isBurstActive && (
            <Zap className="h-4 w-4 text-warning" aria-label={t.profiles.table.burstActive} />
          )}
        </span>
      </TableCell>
      <TableCell>{getRegionLabel(profile.regionId)}</TableCell>
      <TableCell>
        {isDataLoaded ? (
          <span className="text-sm">{formatLastSyncTime(lastRun, t)}</span>
        ) : (
          <Skeleton className="h-4 w-20" />
        )}
      </TableCell>
      <TableCell>
        <span className="text-sm">{profile.isEnabled ? formatNextSyncTime(nextSyncTime) : t.profiles.sync.notScheduledDisabled}</span>
      </TableCell>
      <TableCell>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-sm cursor-help">{sourceTables.length}</span>
            </TooltipTrigger>
            <TooltipContent>
              {sourceTables.length === 0 ? (
                <p>{t.profiles.tables.noTables}</p>
              ) : (
                <div className="max-w-xs">
                  <p className="font-semibold mb-1">{t.profiles.tables.sourceTables}</p>
                  <ul className="list-disc list-inside">
                    {sourceTables.map((table) => (
                      <li key={table.id}>{table.logicalName}</li>
                    ))}
                  </ul>
                </div>
              )}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </TableCell>
      <TableCell>
        {profile.isEnabled ? (
          <span className="inline-flex items-center px-2 py-1 text-xs font-medium background-success text-success rounded-full">
            {t.profiles.status.enabled}
          </span>
        ) : (
          <span className="inline-flex items-center px-2 py-1 text-xs font-medium background-neutral text-neutral rounded-full">
            {t.profiles.status.disabled}
          </span>
        )}
      </TableCell>
      <TableCell>
        {quietTimeCount > 0 ? (
          <span className="text-sm">
            {quietTimeCount}{' '}
            {quietTimeCount !== 1 ? t.profiles.quietTime.schedules : t.profiles.quietTime.schedule}
          </span>
        ) : (
          <span className="text-sm text-muted-foreground">{t.profiles.quietTime.none}</span>
        )}
      </TableCell>
      <TableCell>
        <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
      </TableCell>
    </TableRow>
  );
}

/**
 * Profiles page with list and create functionality
 */
export default function ProfilesPage() {
  const navigate = useNavigate();
  const { orgId } = useParams<{ orgId: string }>();
  const { t } = useLanguage();

  if (!orgId) {
    logger.error(new Error('No orgId found in route params'), {
      context: 'ProfilesPage.mount',
    });
    void navigate('/dashboard');
    return null;
  }

  const { isAdminOrOperator } = useRoles();

  // Fetch profiles list using useQuery (proper for data fetching)
  const {
    data: profilesData,
    isLoading: isLoadingProfiles,
    error: profilesError,
    refetch: refetchProfiles,
  } = useQuery({
    queryKey: ['profiles', 'list', orgId],
    queryFn: async () => {
      const response = await postListProfiles(orgId, { pageSize: 50 });
      return response;
    },
    enabled: !!orgId, // Only run query when orgId is available
    retry: 1,
  });

  // Fetch entitlements to check profile limit
  const { data: entitlementsResponse, error: entitlementsError } =
    useGetOrganizationProductEntitlements(orgId, 'data-mission');

  const entitlements = entitlementsResponse?.data;
  const maxProfiles = entitlements?.count ?? 0;
  const profiles: ProfileDto[] = profilesData?.data.items ?? [];
  const activeProfilesCount = profiles.filter((p) => p.isEnabled).length;
  const atProfileLimit = activeProfilesCount >= maxProfiles;

  const handleView = (profile: ProfileDto) => {
    void navigate(`/dashboard/orgs/${orgId}/profiles/${profile.id}`);
  };

  return (
    <div className="space-y-6">
      <PageHeader title={t.profiles.title} description={t.profiles.description}>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <Button asChild={isAdminOrOperator && !atProfileLimit} disabled={!isAdminOrOperator || atProfileLimit}>
                  {isAdminOrOperator && !atProfileLimit ? (
                    <Link to={`/dashboard/orgs/${orgId}/profiles/wizard/profile`}>
                      <Plus className="mr-2 h-4 w-4" />
                      {t.profiles.createProfile}
                    </Link>
                  ) : (
                    <>
                      <Plus className="mr-2 h-4 w-4" />
                      {t.profiles.createProfile}
                    </>
                  )}
                </Button>
              </div>
            </TooltipTrigger>
            {(!isAdminOrOperator || atProfileLimit) && (
              <TooltipContent>
                <p>
                  {!isAdminOrOperator
                    ? t.profiles.noPermission
                    : `${t.profiles.limitReached} (${String(maxProfiles)})`}
                </p>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      </PageHeader>

      <div className="rounded-lg border bg-card text-card-foreground shadow-sm overflow-hidden">
      {profilesError ? (
        <div className="p-8">
          <QueryErrorState
            error={profilesError}
            message={t.errors.profiles.failedToLoadProfiles}
            onRetry={() => { void refetchProfiles(); }}
          />
        </div>
      ) : entitlementsError ? (
        <div className="p-8">
          <QueryErrorState
            error={entitlementsError}
            message={t.errors.entitlements.failedToLoadEntitlements}
          />
        </div>
      ) : isLoadingProfiles ? (
        <Table className="[&_tr>*:first-child]:pl-8 [&_tr>*:last-child]:pr-8">
          <TableHeader>
            <TableRow>
              <TableHead>{t.profiles.table.name}</TableHead>
              <TableHead>{t.profiles.table.region}</TableHead>
              <TableHead>{t.profiles.table.lastSync}</TableHead>
              <TableHead>{t.profiles.table.nextSync}</TableHead>
              <TableHead>{t.profiles.table.tables}</TableHead>
              <TableHead>{t.profiles.table.status}</TableHead>
              <TableHead>{t.profiles.table.quietTime}</TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {[1, 2, 3].map((i) => (
              <TableRow key={i}>
                <TableCell>
                  <Skeleton className="h-4 w-32" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-20" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-20" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-20" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-12" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-6 w-16 rounded-full" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-16" />
                </TableCell>
                <TableCell></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : profiles.length === 0 ? (
        <div className="text-center text-muted-foreground py-8">
          <p>{t.profiles.noProfiles}</p>
        </div>
      ) : (
        <Table className="[&_tr>*:first-child]:pl-8 [&_tr>*:last-child]:pr-8">
          <TableHeader>
            <TableRow>
              <TableHead>{t.profiles.table.name}</TableHead>
              <TableHead>{t.profiles.table.region}</TableHead>
              <TableHead>{t.profiles.table.lastSync}</TableHead>
              <TableHead>{t.profiles.table.nextSync}</TableHead>
              <TableHead>{t.profiles.table.tables}</TableHead>
              <TableHead>{t.profiles.table.status}</TableHead>
              <TableHead>{t.profiles.table.quietTime}</TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {profiles.map((profile) => (
              <ProfileRow
                key={profile.id}
                profile={profile}
                orgId={orgId}
                onView={() => { handleView(profile); }}
                t={t}
              />
            ))}
          </TableBody>
        </Table>
      )}
      </div>
    </div>
  );
}
